import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { toolImg } from '../shell.models';

/** What the woman is being told about this tool, on every screen alike. */
export type ToolCardStatus = 'done' | 'todo' | 'none';

/**
 * ONE tool card (Revital item 35, Danna 22.09).
 *
 * A tool tile used to be drawn in three different places with three different
 * shapes, so the catalogue showed a photo and a name while home showed a
 * subtitle, a duration and a status. That is the same object described three
 * ways. This is the single card: SAME image ratio, SAME statuses, subtitle and
 * duration wherever they exist.
 *
 * It is presentational only. Who is done, who is in the plan and what happens
 * on a tap all stay with the screen that owns them.
 */
@Component({
  selector: 'app-tool-card',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <button
      class="tc"
      type="button"
      [class.done]="status === 'done'"
      [class.locked]="locked"
      [class.selected]="selected"
      [class.dismissed]="dismissed || removed"
      [attr.aria-pressed]="removable ? removed : null"
      (click)="pick.emit(toolId)"
    >
      <span class="tc-img"><img [src]="img" alt="" loading="lazy" /></span>

      <span class="tc-body">
        <span class="tc-name">{{ name }}</span>
        <span class="tc-desc" *ngIf="desc">{{ desc }}</span>

        <!-- The reason this tool is here, in her own data's terms. Only ever
             shown when the screen actually has one; never a filler sentence. -->
        <span class="tc-why" *ngIf="why">{{ why }}</span>

        <span class="tc-meta">
          <span class="tc-mins" *ngIf="mins && status !== 'done'">
            {{ 'shell.home.minutes' | translate: { n: mins } }}
          </span>
          <span class="tc-todo" *ngIf="status === 'todo' && !locked">
            {{ 'shell.home.toDo' | translate }}
          </span>
          <span class="tc-check" *ngIf="status === 'done' && !locked" aria-hidden="true">✓</span>
        </span>
      </span>

      <span class="lock-badge" *ngIf="locked" aria-hidden="true">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
          <rect x="5" y="11" width="14" height="9" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
      </span>
    </button>

    <!-- "Not for me" lives OUTSIDE the card button, so declining is never a
         mis-tap on opening the tool. Declining does NOT make the tool vanish
         (Danna 22.09): the card stays put, greys back, and says it can be
         undone. It simply stops being offered on the next visit. -->
    <button
      class="tc-dismiss"
      type="button"
      *ngIf="dismissable && !dismissed"
      (click)="dismiss.emit(toolId)"
    >
      {{ 'shell.home.rec.noThanks' | translate }}
    </button>
    <button
      class="tc-undo"
      type="button"
      *ngIf="dismissable && dismissed"
      (click)="undo.emit(toolId)"
    >
      {{ 'shell.home.rec.undo' | translate }}
    </button>

    <!-- Editing her routine on home (Danna 26.09). The same toggle as the card
         tap: the tool stays where it is, greyed, until she taps Done. A second
         tap puts it back. No separate "undo". -->
    <button
      class="tc-remove"
      type="button"
      *ngIf="removable"
      [class.on]="removed"
      [disabled]="busy"
      (click)="pick.emit(toolId)"
    >
      {{ (removed ? 'shell.home.edit.restore' : 'shell.home.edit.remove') | translate }}
    </button>
  `,
  styleUrl: './tool-card.component.scss',
})
export class ToolCardComponent {
  @Input({ required: true }) toolId = '';
  @Input({ required: true }) name = '';
  @Input() desc = '';
  /** One line saying why THIS tool is suggested to HER. */
  @Input() why = '';
  @Input() mins?: number;
  @Input() status: ToolCardStatus = 'none';
  @Input() locked = false;
  @Input() selected = false;
  @Input() dismissable = false;
  /** She declined it. The card stays, quietened, until the next visit. */
  @Input() dismissed = false;

  /** Home edit mode: the card carries a remove toggle under it. */
  @Input() removable = false;
  /** Taken out of her routine in this edit session. Still shown, greyed. */
  @Input() removed = false;
  @Input() busy = false;

  @Output() pick = new EventEmitter<string>();
  @Output() dismiss = new EventEmitter<string>();
  @Output() undo = new EventEmitter<string>();

  get img(): string {
    return toolImg(this.toolId);
  }
}
